import { Component, ViewChild } from '@angular/core';
import { ICellEditorParams } from 'ag-grid-community';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { CurrencyPipe } from '@angular/common';
import { select, Store } from '@ngrx/store';
import { selectCostBasis, selectPositionLotTableData } from '../../../create-request/reducers';
import { take, takeUntil } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { InternalTransferActions } from '../../../create-request/actions';
import { positionConstants } from '../../utilities/Constants';
import { LotDetails } from '../../../create-request/models/internal-transfer/LotDetails';
import { TooltipPosition } from '@angular/material/tooltip';
import { selectViewInContext } from '../../reducers';

@Component({
  standalone: false,
    selector: 'mm-lot-text-input-editor',
    templateUrl: './table-text-lot-input-field.component.html',
    styleUrls: ['./table-text-lot-input-field.component.scss']
})
export class InputFieldLotComponent implements ICellEditorAngularComp {
    constructor(private store: Store, private currencyPipe: CurrencyPipe) { }
    @ViewChild('inputShares') textInput: any;
    @ViewChild('tooltip') tooltip: any;
    params: ICellEditorParams;
    showError: boolean;
    toolTipError: null | string | undefined = '';
    currentValue: null | string | undefined = '';
    currentTextInput: string;
    showIcon: boolean = false;
    isInputField: boolean = true;
    createRequestStep$: Observable<string>;
    position: TooltipPosition = 'after';
    destroy$: Subject<boolean> = new Subject<boolean>();
    costBasisMethod: string | undefined;
    backSpaceDeleteClicked: boolean = false;

    ngOnInit(): void {
        this.createRequestStep$ = this.store.select(selectViewInContext);

    }

    agInit(params: ICellEditorParams): void {
        this.store.select(selectCostBasis).pipe(takeUntil(this.destroy$)).subscribe(info => this.costBasisMethod = info);
        // do not run agInit if backspace or delete key pressed
        if (params.eventKey !== 'Backspace' && params.eventKey !== "Delete" && this.backSpaceDeleteClicked !== true) {
            this.params = params;
            if (!this.params.value || this.params.value === '0') {
                this.params.value = '';
                this.updateRowValue('Estimated market value', '');        }
            const lotsToMoveString: string = this.lotLevelData?.['Lots to move']?.toString() ?? '';
            const lotsToMove: number = parseFloat(lotsToMoveString);


            if (lotsToMove > parseFloat(this.lotLevelData?.['Holding Period']?.replaceAll(',', ''))) {
                this.setUpMinimumError();
                this.updateStoreSharesToMove('',true)
            }
            else if (lotsToMoveString?.split('.')[1]?.length > 6) {
                this.setUpLotLevelFractionalError();
                this.updateStoreSharesToMove('',true)
            }
            else {
                this.clearErrors();
            }

            if (this.positionRowData['detailRows'] !== undefined) {
                if (this.positionRowData['detailRows'].length < this.positionRowData['lotDetail'].length) {
                    this.positionRowData['detailRows'].push(this.params);
                }
            }
            this.backSpaceDeleteClicked = false;
        }
    }

    get isManualFlow() {
        let displayTextBox = this.shouldTextBoxRender(this.lotLevelData?.restrictions?.isRestricted ?? false)
        return this.costBasisMethod === 'None' && displayTextBox
    }

    shouldTextBoxRender(isRestricted: boolean): boolean {
        return this.costBasisMethod === 'None' && !isRestricted;
    }

    get positionRowData() {
        return this.params?.data?.masterNode?.data;
    }

    get lotLevelData() {
        return this.params?.data;
    }

    setlotLevelData(colKey: string, value: string) {
        this.params.data[colKey] = this.params?.data != null ? value : '';
    }

    afterGuiAttached() {
        this.textInput?.nativeElement?.focus();
    }

    // Returns value entered into textbox.
    getValue() {
        return this.textInput?.nativeElement?.value;
    }

    onKeyPress(event: any) {
        if (!isNumeric(event)) {
            event.preventDefault();
        }
        function isNumeric(event: any) {
            return !!/\d/.test(event.key) || !!/\./.test(event.key);
        }
    }

    onkeydown(event: KeyboardEvent) {
        if(event.code === 'Enter' || event.code === 'Tab') {
            this.onchange();
        } else if (event.code === 'Backspace') {
            let selection = document.getSelection();
            let selectionString = selection?.toString() ?? '';
            let inputString = this.getValue()?.toString() ?? '';
            // for some reason, backspace only deletes one char when dragging to select all (selecting a few chars with backspace works fine)
            // this code deletes the whole string
            if (selectionString.length > 0 && selectionString.length === inputString.length) {
                event.stopPropagation();
                this.textInput.nativeElement.value = '';
                selection?.removeRange(selection.getRangeAt(0));
                this.onchange();
            }
        }
        if(event.code === 'Backspace' || event.code === 'Delete'){
            this.backSpaceDeleteClicked = true;
        }
    }

    onMouseDown(event: MouseEvent) {
        if (this.textInput?.nativeElement !== document.activeElement) {
            this.textInput?.nativeElement?.focus();
        }
    }


    isCancelAfterEnd(): boolean {
        this.onchange();
        return false;
    }

    onchange() {
        if (!this.isManualFlow){
            return;
        }
        if (this.getValue() === '') {
            this.clearErrors();
        }
        if (this.lotLevelData?.['Lots to move']?.toString() === this.getValue()?.toString()) {
            return;
        }
        this.currentTextInput = this.lotLevelData?.['Lots to move'];

        if (!Number.isNaN(Number(this.getValue()))) {
            this.textInput.nativeElement.value = !!this.getValue() ? parseFloat(this.getValue()) : '';
            if (Number(this.getValue()) === 0){
                this.textInput.nativeElement.value = '';
            }
            const isValidLotValue = !this.isManualSelection() || this.isValidLotInputAndSetErrorIfApplicable(this.getValue());
            const initialSharesToMove = this.positionRowData?.['Shares to move'] !== '' ? Number(this.positionRowData?.['Shares to move']) : 0;
            const initialLotsToMove = this.currentTextInput !== '' && this.isValidLotInput(this.currentTextInput)
                && Number(this.currentTextInput) <= Number(this.lotLevelData?.['Holding Period']?.split(' ')[0]?.replaceAll(',', '')) ? Number(this.currentTextInput) : 0;
            if (!isValidLotValue) {
                this.updateRowValue('Lots to move', parseFloat(this.getValue()))
                this.updateRowValue('Estimated market value', '');
                this.updateMasterRowSharesToMove(initialSharesToMove - initialLotsToMove);
                return;
            }
            if (this.currentTextInput !== this.getValue() && !isNaN(Number(this.lotLevelData?.['Holding Period']?.split(' ')[0]?.replaceAll(',', '')))) {
                const inputChange = this.getValue() !== '' ? Number(this.getValue()) : 0;
                var valueChange = this.getValueChange(initialSharesToMove, inputChange, initialLotsToMove);

                if (valueChange !== undefined) {
                    if (Number.isInteger(inputChange) && !Number.isInteger(initialLotsToMove)){
                        valueChange = this.fixFloatError(valueChange);
                    }
                    this.updateMasterRowSharesToMove(valueChange);
                    this.updateStoreSharesToMove(valueChange.toString(), true);
                }
                else {
                    this.updateMasterRowSharesToMove('');
                    this.updateStoreSharesToMove('', true);
                }
                if (inputChange !== 0 && inputChange !== undefined) {
                    this.params.value = this.getValue();
                    this.updateRowValue('Lots to move', parseFloat(this.getValue()));
                    this.updateRowValue('Estimated market value',
                        this.currencyPipe.transform(Number(inputChange) * Number(this.positionRowData?.['Current price/share']?.replace(',', '')?.replace('$', '')) * Number(this.positionRowData?.['valMultiplier'])));
                }
                else {
                    this.updateRowValue('Lots to move', '');
                    this.updateRowValue('Estimated market value', '');
                }
            }
            else if (this.getValue() === '' && this.currentTextInput !== '') {
                this.updateRowValue('Lots to move', '');
                this.updateRowValue('Estimated market value', '');
                var currentSharesToMove = Number(this.positionRowData?.['Shares to move']);
                var masterSharesToMove = currentSharesToMove - Number(this.currentTextInput);
                this.updateMasterRowSharesToMove(masterSharesToMove);
                this.updateStoreSharesToMove(masterSharesToMove.toString(), true);
            }
        }
        else {
            if (this.textInput !== undefined) {
                this.textInput.nativeElement.value = '';
            }
            if (this.currentTextInput !== '') {
                this.updateRowValue('Lots to move', '');
                this.updateRowValue('Estimated market value', '');
                var currentSharesToMove = Number(this.positionRowData?.['Shares to move']);
                var masterSharesToMove = currentSharesToMove - Number(this.currentTextInput);
                this.updateMasterRowSharesToMove(masterSharesToMove);
                this.updateStoreSharesToMove(masterSharesToMove.toString(), true);
            }
        }
        this.agInit(this.params);
    }

    isValidLotInputAndSetErrorIfApplicable(inputValue: string) {
        if (parseFloat(inputValue) > parseFloat(this.lotLevelData?.['Holding Period']?.replaceAll(',', ''))) {
            this.setUpMinimumError();
            return false;
        } else if (inputValue?.split('.')[1]?.length > 6) {
            this.setUpLotLevelFractionalError();
            return false;
        }
        else {
            return true;
        }
    }

    isValidLotInput(inputValue: string) {
        const inputSplit = inputValue?.toString()?.split('.') ?? [];
        if (parseFloat(inputValue) > parseFloat(this.lotLevelData?.['Holding Period']?.replaceAll(',', ''))) {
            return false;
        } else if (inputSplit &&  inputSplit.length > 1 && inputSplit[1]?.length > 6) {
            return false;
        } else {
            return true;
        }
    }

    isManualSelection() {
        return this.lotLevelData?.['Acquired date'] !== undefined;
    }

    updateStoreSharesToMove(sharesToMove: string, lotUpdate: boolean) {

        this.store.pipe(select(selectPositionLotTableData), take(1)).subscribe((rowData) => {
            let positionData = rowData.map(position => {
                return {
                    'Symbol': position['Symbol'],
                    'Name': position['Name'],
                    'RowHeight': position['RowHeight'],
                    'Available shares': position['Available shares'],
                    'Current price/share': position['Current price/share'],
                    'Cost basis': position['Cost basis'],
                    'Shares to move': (this.costBasisMethod !== 'None') ? '' : (position.Symbol == this.lotLevelData?.['Symbol']) ? sharesToMove : position['Shares to move'],
                    'Estimated market value': position['Estimated market value'],
                    'setToAverage': position['setToAverage'],
                    'lotDetail': ((lotUpdate && (this.costBasisMethod === 'None')) ? this.updateLotsToMove(position['lotDetail'], this.lotLevelData['lotId'], this.getValue(), this.costBasisMethod) : position['lotDetail']),
                    'restrictions': position['restrictions'],
                    'Quantity': position['Quantity'],
                    'cmtSequenceId': position['cmtSequenceId'],
                    'valMultiplier': position['valMultiplier'],
                    'schwabSecurityId': position['schwabSecurityId'],
                    'nodeCostBasis': this.costBasisMethod
                };
            });
            this.store.dispatch(InternalTransferActions.updateRowData({ response: positionData }));
        });
    }

    updateStorePosition(symbolName: string, sharesToMove: string, costBasis: string) {
        this.store.pipe(select(selectPositionLotTableData), take(1)).subscribe((rowData) => {
            let positionData = rowData.map(position => {
                if (position['Symbol'] === symbolName) {
                    return {
                        ...position,
                        'Shares to move': sharesToMove,
                        'nodeCostBasis': this.costBasisMethod
                    };
                }
                else {
                    return {
                        ...position,
                        'nodeCostBasis': this.costBasisMethod
                    };
                }

            });
            this.store.dispatch(InternalTransferActions.updateRowData({ response: positionData }));
        });
    }

    updateLotsToMove(lots: LotDetails[], id: string, lotsToMove: string, costBasis: string) {
        let lotData = lots.map((lot: any) => {
            if (id === lot['lotId']) {
                return {
                    ...lot,
                    'lotsToMove': (this.costBasisMethod === 'None') ? lotsToMove : ''
                };
            }
            else {
                return {
                    ...lot,
                    'lotsToMove': (this.costBasisMethod === 'None') ? lot['lotsToMove'] : ''
                };
            }
        });
        return lotData;
    }

    updateMasterRowSharesToMove(value: string | number) {
        this.params.data.masterNode.node?.setDataValue('Shares to move', value.toString());
    }

    updateRowValue(colKey: string, value: any) {
        this.params.node?.setDataValue(colKey, value.toString());
    }


    setUpLotLevelFractionalError() {
        this.params?.node?.setRowHeight(positionConstants.tableLotFractionalRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
        this.currentValue = "Enter a value with no more than 6 decimals.";
        this.showIcon = false;
    }

    setUpMinimumError() {
        this.params?.node?.setRowHeight(positionConstants.tableMinimumErrorRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
        this.currentValue = "Enter a quantity equal to or less than available quantity.";
        this.showIcon = false;
    }



    clearErrors() {
        this.currentValue = "";
        this.showIcon = false;
        this.params?.node?.setRowHeight(positionConstants.tableRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
    }

    focusOutFunction() {
        this.finishedEditingPressed('Enter');
    }

    finishedEditingPressed(event: any) {
        const key = event.key;
        return key === 'Enter' || key === 'Tab';
    }

    fixFloatError(value: number){
        const tolerance = 1e-10;
        return Math.abs(value - Math.round(value)) < tolerance ? Math.round(value): value;
    }

    getValueChange(initialSharesToMove: number, inputChange: number, initialLotsToMove: number) {
        let decimalLength = (numStr: number) => {
            var pieces = numStr.toString().split(".");
            if (!pieces[1]) return 0;
            return pieces[1].length;
        };
        let precision = Math.pow(10, Math.max(decimalLength(initialSharesToMove), decimalLength(inputChange), decimalLength(initialLotsToMove)));
        return (Math.round(initialSharesToMove * precision) + Math.round(inputChange * precision) - Math.round(initialLotsToMove * precision)) / precision;
    }
}